/**
 * Seeds bootstrap biblical text (KJV, public domain) AND Strong lexicon
 * of CONCORDANCE into local PowerSync database, so READER and
 * concordance work completely offline from first launch, without
 * depender del servidor.
 *
 * Tables `bible_verse_content` and `strong_entry` are marked
 * `localOnly` in schema (see packages/powersync/src/schema.ts), so these
 * writes do NOT enter upload queue (ps_crud) nor trigger migration
 * dialog.
 *
 * Idempotency: generation marker persisted in localStorage. If seeded
 * generation matches current, skips re-insertion. Bumping
 * `SEED_GENERATION` forces re-seeding even if row count is unchanged
 * (required when adding Strong markup without changing row count).
 */

import { bible_verse_content, eq, type PowerSyncDrizzleDB, strong_entry } from "@bso/powersync";
import { KJV_SEED_VERSES, SEED_GENERATION, SEED_VERSION } from "../data/kjv-seed";
import { STRONG_LEXICON } from "../data/strong-lexicon";
import { STRONG_TAGGED_VERSES } from "../data/strong-tagged-verses";

const SEED_GENERATION_KEY = "reader-seed-generation";

let seedPromise: Promise<void> | null = null;

function verseId(version: string, book: number, chapter: number, verse: number): string {
  return `${version}-${book}-${chapter}-${verse}`;
}

function coordinateKey(book: number, chapter: number, verse: number): string {
  return `${book}-${chapter}-${verse}`;
}

/** Verse text, with interspersed Strong markup if tagged. */
function verseText(book: number, chapter: number, verse: number, fallback: string): string {
  return STRONG_TAGGED_VERSES[coordinateKey(book, chapter, verse)] ?? fallback;
}

/**
 * Inserts bootstrap subset (text + Strong lexicon) if not yet
 * present for current generation. Resolves once per session
 * (memoized) to avoid querying on every navigation.
 */
export async function seedReaderContent(db: PowerSyncDrizzleDB | null): Promise<void> {
  if (!db) {
    return;
  }
  if (seedPromise) {
    return seedPromise;
  }

  seedPromise = (async () => {
    try {
      const seededGeneration =
        typeof localStorage === "undefined" ? null : localStorage.getItem(SEED_GENERATION_KEY);
      if (seededGeneration === SEED_GENERATION) {
        return;
      }

      // PowerSync tables are VIEWS over ps_data -> UPSERT/onConflict not
      // supported ("cannot UPSERT a view"). For clean re-seeding (new
      // generation) delete first and re-insert with deterministic IDs.
      await db.delete(bible_verse_content).where(eq(bible_verse_content.version, SEED_VERSION));

      const now = new Date().toISOString();
      // Batch insertion to avoid building a massive single query.
      // Sequential by design: preserves deterministic operation order.
      // on local database (non-parallelizable work).
      const BATCH_SIZE = 100;
      for (let i = 0; i < KJV_SEED_VERSES.length; i += BATCH_SIZE) {
        const batch = KJV_SEED_VERSES.slice(i, i + BATCH_SIZE).map((v) => ({
          id: verseId(SEED_VERSION, v.book, v.chapter, v.verse),
          version: SEED_VERSION,
          book: v.book,
          chapter: v.chapter,
          verse: v.verse,
          text: verseText(v.book, v.chapter, v.verse, v.text),
          created_at: now,
          updated_at: now,
        }));
        // oxlint-disable-next-line no-await-in-loop -- siembra secuencial intencional
        await db.insert(bible_verse_content).values(batch);
      }

      // Strong lexicon (offline concordance).
      await db.delete(strong_entry);
      for (let i = 0; i < STRONG_LEXICON.length; i += BATCH_SIZE) {
        const batch = STRONG_LEXICON.slice(i, i + BATCH_SIZE).map((e) => ({
          id: e.code,
          code: e.code,
          lemma: e.lemma,
          transliteration: e.transliteration,
          pronunciation: e.pronunciation,
          gloss: e.gloss,
          definition: e.definition,
          language: e.language,
          created_at: now,
          updated_at: now,
        }));
        // oxlint-disable-next-line no-await-in-loop -- siembra secuencial intencional
        await db.insert(strong_entry).values(batch);
      }

      if (typeof localStorage !== "undefined") {
        localStorage.setItem(SEED_GENERATION_KEY, SEED_GENERATION);
      }
    } catch (error) {
      // Do not block reader startup if seeding fails: loader falls back
      // to server API. Reset to allow subsequent retry.
      seedPromise = null;
      console.warn("[reader] Failed to seed offline biblical text / Strong lexicon", error);
    }
  })();

  return seedPromise;
}
